'use client'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import { gql, useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'

import LanguageSwitcher from '@/components/shared/languageSelect'
import { profile } from 'console'

import { useLanguage } from '@/context/LanguageContext'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { get } from 'http'
import { Checkbox } from '@/components/ui/checkbox'
import '@/components/styles/Form.css' // Import the CSS file
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'

import PaletteSelect from '@/components/shared/inputComponents/PaletteSelect'
import PriceListSelect from '@/components/shared/inputComponents/PriceListSelect'
import {
  CREATE_CUSTOMER_GROUP,
  GET_CUSTOMER_GROUP,
  UPDATE_CUSTOMER_GROUP,
} from './customerGroupQueries'
import AdditionalComponentConstraints from '@/components/AdditionalComponentConstraints'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  fabric_palettes: z.array(z.number()).optional(),
  price_listId: z.number().nullable(),
  additional_components: z.object({}), // Add this field
})

interface MyFormProps {
  data: {
    id: number
    // Add other properties of data as needed
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void // Add this prop
}

export default function CustomerGroupForm({
  data,
  onDirtyChange,
  onRequestClose,
}: MyFormProps) {
  const [id, setId] = useState(data?.id) // Replace currentData with id

  const {
    data: { findUniqueCustomerGroup: customerGroupData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_CUSTOMER_GROUP, {
    variables: {
      id: id, // Use id instead of currentData.id
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<z.infer<typeof formSchema>>({
    // resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      additional_components: [], // Initialize additional_components
      fabric_palettes: [],
      price_listId: null,
    },
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form

  const [updateCustomerGroup] = useMutation(UPDATE_CUSTOMER_GROUP, {
    onError: e => {
      toast.error('Failed to update customer group')
    },
    onCompleted: () => {
      toast.success('Customer group updated successfully')
      reset() // Reset form to clear dirty state
    },
    refetchQueries: ['GET_CUSTOMER_GROUPS', 'GET_CUSTOMER_GROUP'],
  })

  const [createCustomerGroup] = useMutation(CREATE_CUSTOMER_GROUP, {
    onError: e => {
      toast.error('Failed to create customer group')
    },
    onCompleted: response => {
      toast.success('Customer group created successfully')
      const newId = response.createCustomerGroup.id // Assuming the response contains the new ID
      setId(newId) // Set the new id
    },
    refetchQueries: ['GET_CUSTOMER_GROUPS'],
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log('values', values.additional_components)
    if (id) {
      // Use id instead of currentData.id
      updateCustomerGroup({
        variables: {
          id: id, // Use id
          name: values.name,
          fabric_palettes: values.fabric_palettes,
          price_listId: values.price_listId,
          additional_components: Object.values(values.additional_components)
            .flat()
            .filter(item => item)
            .map((item: any) => item.id), // Flatten the,
        },
      })
    } else {
      createCustomerGroup({
        variables: {
          name: values.name,
          fabric_palettes: values.fabric_palettes,
          price_listId: values.price_listId,
          additional_components: Object.values(values.additional_components)
            .flat()
            .filter(item => item) // Flatten the additional_components array
            .map((item: any) => item.id), // Flatten the,
        },
      })
    }
  }

  useEffect(() => {
    if (customerGroupData) {
      const additionalComponents: any = []
      customerGroupData.additional_components.forEach(item => {
        if (
          !additionalComponents[
            item.additionalComponent.additionalComponentGroupId
          ]
        ) {
          additionalComponents[
            item.additionalComponent.additionalComponentGroupId
          ] = []
        }

        additionalComponents[
          item.additionalComponent.additionalComponentGroupId
        ].push({ id: item.additionalComponent.id })
      })

      reset({
        name: customerGroupData.name,
        fabric_palettes: customerGroupData.fabric_palettes.map(
          (palette: any) => palette.id
        ),
        price_listId: customerGroupData.price_listId,
        additional_components: additionalComponents,
      })
    }
  }, [customerGroupData, reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <>
      <SheetContent className="sheet-content flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <div className="flex justify-between items-center">
            <SheetTitle>Customer Group</SheetTitle>
            <LanguageSwitcher />
          </div>
          {/* <SheetDescription></SheetDescription> */}
        </SheetHeader>
        <SheetScrollableContent>
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4 max-w-3xl mx-auto py-5"
            >
              <div className="grid gap-4 py-4">
                <InputFieldContainer
                  label="Group Name"
                  control={control}
                  name={`name`}
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input placeholder="" type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Fabric Palettes"
                  description="Limit fabric palettes for the customers"
                  control={control}
                  name="fabric_palettes"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <PaletteSelect field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Price List"
                  description="Select price list for the customers"
                  control={control}
                  name="price_listId"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <PriceListSelect field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Components"
                  control={control}
                  name="additional_components"
                  description="Limit component options for the customers"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <AdditionalComponentConstraints field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={loadingData}>
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </Form>
        </SheetScrollableContent>
      </SheetContent>
    </>
  )
}
